import type PriceRecommendationRoomState from '@app/model/market_state/PriceRecommendationRoomState';
import type HotelRoomMappingType from '@app/model/market_state/HotelRoomMappingType';

// The rules of the percent popup of the date table.
// The Market estimate (R1), the untouched percent (R6), the hint (R4) and the PMS rates list (R5).

export type PmsRateRow = { roomId: number, roomName: string, basePrice: number, newPrice: number };
export type PmsRateGroup = { roomMappingType: HotelRoomMappingType, rows: PmsRateRow[] };
type StateLike = { roomMappingType: HotelRoomMappingType, minimumBasePriceRoomStates: PriceRecommendationRoomState[] };

// R1. Untouched: the Rec Price of the cell. Typed: the Hotel Market Price × (1 + p / 100).
export const marketEstimate = (hotelMarketPrice: number | undefined, recommendedPrice: number | undefined, typedPercent: number | undefined): number | undefined => {
	if (typedPercent === undefined) return recommendedPrice;
	if (hotelMarketPrice === undefined) return undefined;
	return hotelMarketPrice * (1 + typedPercent / 100);
};

// R6. The field still holds the pre-fill (the rounded percent of the cell): the check does what the plane does.
export const isUntouchedPercent = (typed: string, shownPercent: number): boolean => typed.trim() !== '' && Number.isFinite(Number(typed)) && Number(typed) === shownPercent;

// R5. One row per room of each state: the lowest Base Price among the occupancy rows of the room; the anchor
// first, then the derivatives by Base Price. Untouched: the Final Price of that row. Typed: round(base × (1 + p / 100)).
export const pmsRateGroups = (states: StateLike[], typedPercent: number | undefined): PmsRateGroup[] => states.map(state => {
	const byRoom = new Map<number, PriceRecommendationRoomState>();
	for (const room of state.minimumBasePriceRoomStates) {
		const current = byRoom.get(room.roomId);
		if (current === undefined || room.basePrice < current.basePrice) byRoom.set(room.roomId, room);
	}
	const rows = Array.from(byRoom.values())
		.sort((a, b) => {
			if (a.isAnchor !== b.isAnchor) return a.isAnchor ? -1 : 1;
			if (a.basePrice !== b.basePrice) return a.basePrice - b.basePrice;
			return a.roomName.localeCompare(b.roomName);
		})
		.map(room => ({
			roomId: room.roomId,
			roomName: room.roomName,
			basePrice: room.basePrice,
			newPrice: typedPercent === undefined ? room.finalPrice : Math.round(room.basePrice * (1 + typedPercent / 100)),
		}));
	return { roomMappingType: state.roomMappingType, rows };
});

export const countRooms = (groups: PmsRateGroup[]): number => new Set(groups.flatMap(group => group.rows.map(row => row.roomId))).size;

// R4. `1 room type · 3 rooms`.
export const editorHint = (types: number, rooms: number): string =>
	`${types} room type${types === 1 ? '' : 's'} · ${rooms} room${rooms === 1 ? '' : 's'}`;
